/**
 * @fileoverview Context-menu content for Library Finder items.
 *
 * Rendered by the vendored Finder's `renderItemMenu` extension inside a
 * Radix ContextMenuContent. Menus only dispatch — dialog state (move,
 * rename, delete, new subfolder) lives in LibraryView so it survives the
 * menu closing.
 */

import {
  Download,
  Eye,
  FolderInput,
  FolderPlus,
  Pencil,
  RefreshCw,
  Tag,
  Trash2,
} from "lucide-react";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/ui/composed/menus/context-menu";
import { CATEGORY_LABELS } from "../constants";

/** A selected Finder item resolved against the Library manifest meta. */
export interface LibraryMenuItem {
  kind: "file" | "folder";
  /** Finder path (trailing slash for folders). */
  path: string;
  name: string;
  documentId?: Id<"documents">;
  folderId?: Id<"folders">;
  url?: string | null;
  category?: string;
  /** Folder originates from a drive sync connection. */
  isSynced: boolean;
}

export interface LibraryMenuActions {
  onOpenDocument: (item: LibraryMenuItem) => void;
  onDownload: (item: LibraryMenuItem) => void;
  onMoveTo: (items: LibraryMenuItem[]) => void;
  onSetCategory: (items: LibraryMenuItem[], category: string) => void;
  onRename: (item: LibraryMenuItem) => void;
  onReprocess: (items: LibraryMenuItem[]) => void;
  onDelete: (items: LibraryMenuItem[]) => void;
  onNewSubfolder: (item: LibraryMenuItem) => void;
}

const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS).filter(
  ([key]) => key !== "all",
);

function SetCategorySubmenu({
  files,
  onSetCategory,
}: {
  files: LibraryMenuItem[];
  onSetCategory: LibraryMenuActions["onSetCategory"];
}) {
  // A shared category across the selection gets a subtle marker.
  const current =
    files.length > 0 && files.every((f) => f.category === files[0].category)
      ? files[0].category
      : undefined;

  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger>
        <Tag className="mr-2 h-4 w-4 text-muted-foreground" />
        Set category
      </ContextMenuSubTrigger>
      <ContextMenuSubContent className="max-h-80 overflow-y-auto">
        {CATEGORY_OPTIONS.map(([value, label]) => (
          <ContextMenuItem
            key={value}
            onSelect={() => onSetCategory(files, value)}
            className={value === current ? "bg-accent" : undefined}
          >
            {label}
          </ContextMenuItem>
        ))}
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

/**
 * Menu items for the current selection. Single file: open / download /
 * move / category / rename / re-run / delete. Single folder: new subfolder
 * / rename / move / delete. Multi-selections drop the single-item actions
 * (Open, Download, Rename) and apply the rest in bulk.
 */
export function LibraryItemMenu({
  items,
  actions,
}: {
  items: LibraryMenuItem[];
  actions: LibraryMenuActions;
}) {
  if (items.length === 0) return null;

  const files = items.filter((item) => item.kind === "file");
  const single = items.length === 1 ? items[0] : null;

  if (single?.kind === "folder") {
    return (
      <>
        <ContextMenuItem onSelect={() => actions.onNewSubfolder(single)}>
          <FolderPlus className="mr-2 h-4 w-4 text-muted-foreground" />
          New subfolder…
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => actions.onRename(single)}>
          <Pencil className="mr-2 h-4 w-4 text-muted-foreground" />
          Rename…
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => actions.onMoveTo(items)}>
          <FolderInput className="mr-2 h-4 w-4 text-muted-foreground" />
          Move to…
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          onSelect={() => actions.onDelete(items)}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete…
        </ContextMenuItem>
      </>
    );
  }

  if (single?.kind === "file") {
    return (
      <>
        <ContextMenuItem onSelect={() => actions.onOpenDocument(single)}>
          <Eye className="mr-2 h-4 w-4 text-muted-foreground" />
          Open
        </ContextMenuItem>
        <ContextMenuItem
          disabled={!single.url}
          onSelect={() => actions.onDownload(single)}
        >
          <Download className="mr-2 h-4 w-4 text-muted-foreground" />
          Download
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={() => actions.onMoveTo(items)}>
          <FolderInput className="mr-2 h-4 w-4 text-muted-foreground" />
          Move to…
        </ContextMenuItem>
        <SetCategorySubmenu
          files={files}
          onSetCategory={actions.onSetCategory}
        />
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={() => actions.onRename(single)}>
          <Pencil className="mr-2 h-4 w-4 text-muted-foreground" />
          Rename…
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => actions.onReprocess(items)}>
          <RefreshCw className="mr-2 h-4 w-4 text-muted-foreground" />
          Re-run processing
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          onSelect={() => actions.onDelete(items)}
          className="text-destructive focus:text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete…
        </ContextMenuItem>
      </>
    );
  }

  // Multi-selection (possibly mixed files + folders).
  return (
    <>
      <ContextMenuItem onSelect={() => actions.onMoveTo(items)}>
        <FolderInput className="mr-2 h-4 w-4 text-muted-foreground" />
        Move {items.length} items to…
      </ContextMenuItem>
      {files.length > 0 && (
        <SetCategorySubmenu
          files={files}
          onSetCategory={actions.onSetCategory}
        />
      )}
      {files.length > 0 && (
        <ContextMenuItem onSelect={() => actions.onReprocess(files)}>
          <RefreshCw className="mr-2 h-4 w-4 text-muted-foreground" />
          Re-run processing
        </ContextMenuItem>
      )}
      <ContextMenuSeparator />
      <ContextMenuItem
        onSelect={() => actions.onDelete(items)}
        className="text-destructive focus:text-destructive"
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Delete {items.length} items…
      </ContextMenuItem>
    </>
  );
}
